import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Logo from './Logo';
import { Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TEMPLATES = {
  thanksgiving: {
    title: 'Happy Thanksgiving!',
    subtitle: 'From Our Family to Yours',
    bg: 'bg-gradient-to-br from-orange-600 via-amber-700 to-yellow-600',
    border: 'from-orange-500 via-amber-500 to-yellow-500',
    headingColor: 'text-orange-600',
    decor: ['🍂', '🍁', '🍁', '🍂'],
    inline: ['🦃', '🥧', '🌽'],
    defaultMsg: "We're grateful for your trust and partnership. Thank you for choosing HVAC Best Supply for all your refrigerant needs. Wishing you and your loved ones a warm Thanksgiving filled with joy, gratitude, and delicious moments!",
  },
  christmas: {
    title: 'Merry Christmas!',
    subtitle: 'Season\u2019s Greetings From All of Us',
    bg: 'bg-gradient-to-br from-red-700 via-emerald-700 to-red-800',
    border: 'from-red-500 via-emerald-500 to-red-500',
    headingColor: 'text-red-600',
    decor: ['🎄', '❄️', '❄️', '🎄'],
    inline: ['🎁', '⛄', '🔔'],
    defaultMsg: 'Wishing you and your family a Merry Christmas filled with warmth, joy, and time with loved ones. Thank you for being a valued part of our HVAC Best Supply family this year.',
  },
  newyear: {
    title: 'Happy New Year!',
    subtitle: 'Cheers to What\u2019s Ahead',
    bg: 'bg-gradient-to-br from-slate-900 via-blue-900 to-yellow-700',
    border: 'from-yellow-400 via-amber-300 to-yellow-400',
    headingColor: 'text-amber-600',
    decor: ['✨', '🎆', '🎆', '✨'],
    inline: ['🥂', '🎉', '🪩'],
    defaultMsg: 'Thank you for an incredible year. We could not have done it without you. Wishing you a healthy, prosperous, and happy New Year from all of us at HVAC Best Supply.',
  },
  july4: {
    title: 'Happy 4th of July!',
    subtitle: 'Celebrating With You',
    bg: 'bg-gradient-to-br from-blue-900 via-red-700 to-white',
    border: 'from-red-600 via-white to-blue-600',
    headingColor: 'text-blue-700',
    decor: ['🎆', '🇺🇸', '🇺🇸', '🎆'],
    inline: ['🦅', '🎇', '⭐'],
    defaultMsg: 'Wishing you a safe and joyful Independence Day. Thank you for keeping America\u2019s homes and businesses cool — and thank you for trusting HVAC Best Supply.',
  },
  generic: {
    title: 'Thank You!',
    subtitle: 'From All of Us at HVAC Best Supply',
    bg: 'bg-gradient-to-br from-blue-900 via-blue-700 to-blue-500',
    border: 'from-blue-500 via-blue-400 to-blue-500',
    headingColor: 'text-blue-700',
    decor: ['💙', '✨', '✨', '💙'],
    inline: ['🙏', '🤝', '⭐'],
    defaultMsg: 'Thank you for being a valued customer. We appreciate your business and look forward to serving you. Please reach out anytime — we\u2019re here to help.',
  },
};

const SeasonalGreeting = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/greeting`)
      .then((r) => setData(r.data))
      .catch(() => setData({ template: 'generic', enabled: true }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <Loader2 className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  if (data && data.enabled === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 p-8">
        <div className="bg-white rounded-2xl shadow-xl p-10 max-w-md text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">HVAC Best Supply</h1>
          <p className="text-gray-600">Thanks for stopping by! Visit <a href="/" className="text-blue-600 font-semibold">hvacbestsupply.com</a> for premium refrigerants.</p>
        </div>
      </div>
    );
  }

  const tpl = TEMPLATES[data?.template] || TEMPLATES.thanksgiving;
  const message = (data?.custom_message && data.custom_message.trim()) || tpl.defaultMsg;

  return (
    <div className={`min-h-screen ${tpl.bg} flex items-center justify-center p-4 sm:p-8`} data-testid={`greeting-page-${data?.template || 'thanksgiving'}`}>
      <div className="max-w-4xl w-full">
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          <div className={`h-4 bg-gradient-to-r ${tpl.border}`}></div>

          <div className="p-8 md:p-16 text-center relative">
            <div className="absolute top-8 left-8 text-5xl md:text-6xl opacity-20 select-none">{tpl.decor[0]}</div>
            <div className="absolute top-12 right-12 text-4xl md:text-5xl opacity-20 select-none">{tpl.decor[1]}</div>
            <div className="absolute bottom-12 left-16 text-4xl md:text-5xl opacity-20 select-none">{tpl.decor[2]}</div>
            <div className="absolute bottom-8 right-8 text-5xl md:text-6xl opacity-20 select-none">{tpl.decor[3]}</div>

            <div className="flex justify-center mb-6 md:mb-8 relative z-10">
              <div className="bg-gray-50 p-4 md:p-6 rounded-2xl shadow-lg">
                <Logo />
              </div>
            </div>

            <div className="relative z-10 space-y-5 md:space-y-6">
              <h1 className={`text-4xl md:text-6xl font-black ${tpl.headingColor} mb-4`}>{tpl.title}</h1>

              <div className="flex items-center justify-center gap-4 mb-4">
                {tpl.inline.map((e, i) => (
                  <div key={i} className="text-3xl md:text-4xl">{e}</div>
                ))}
              </div>

              <p className="text-xl md:text-3xl text-gray-700 font-semibold mb-4">{tpl.subtitle}</p>

              <div className="max-w-2xl mx-auto">
                <p className="text-base md:text-xl text-gray-600 leading-relaxed whitespace-pre-line">{message}</p>
              </div>

              <div className="mt-8 md:mt-10 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-2xl p-5 md:p-6 max-w-xl mx-auto">
                <p className="text-xl md:text-2xl font-bold text-blue-700 mb-1">Volume Discounts Always Available</p>
                <p className="text-base md:text-lg text-gray-700 font-semibold">Free Shipping on All Orders</p>
                <a href="/" className="text-sm text-blue-600 hover:underline mt-2 inline-block">Visit hvacbestsupply.com →</a>
              </div>

              <div className="mt-6 md:mt-8 pt-6 md:pt-8 border-t-2 border-gray-200">
                <div className="flex flex-col md:flex-row items-center justify-center gap-3 md:gap-6 text-gray-600 text-sm md:text-base">
                  <span>hvacbestsupply@gmail.com</span>
                  <span className="hidden md:block text-gray-400">|</span>
                  <span>+1 562-416-1615</span>
                  <span className="hidden md:block text-gray-400">|</span>
                  <span>Downey, California</span>
                </div>
              </div>
            </div>
          </div>

          <div className={`h-4 bg-gradient-to-r ${tpl.border}`}></div>
        </div>

        <div className="text-center mt-6">
          <p className="text-white text-base md:text-lg font-semibold drop-shadow-lg">From the HVAC Best Supply Family</p>
        </div>
      </div>
    </div>
  );
};

export default SeasonalGreeting;
